import { makeAutoObservable, runInAction } from "mobx";
import BillingService from "../../../services/api/billing-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import toastMessageListStore from "../../../components/toast-messages-modal/toast-messages-store";
import lastSbrReceiptStore from "./last-sbr-receipt-store";

export interface ISbrReceipt {
    id: number;
    date_from: string;
    date_to: string;
    company_tax_id?: string | undefined;
    other?: string | undefined;
    receipt_data?: string | undefined;
}

export interface ISbrReceiptRequest extends Partial<Omit<ISbrReceipt, "id">> {}

class SbrReceiptRequestStore {
    private _request: ISbrReceiptRequest;
    private _isGeneratingInvoice: boolean = false;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });   
        /** Initialize request */    
        this._request = {} as ISbrReceiptRequest;
    }

    private get service(): BillingService {
        return rootContainer.get<BillingService>(TYPE.BillingService);
    }

    public get isGeneratingInvoice(): boolean {
        return this._isGeneratingInvoice;
    }

    public get isBusy(): boolean {
        return this.isGeneratingInvoice || lastSbrReceiptStore.isLoading;
    }

    public get dateFrom(): string | undefined {
        return this._request.date_from;
    }

    public get dateTo(): string | undefined {
        return this._request.date_to;
    }

    public get dateFromDateString(): string | undefined {
        return this._dateTimeToDateString(this.dateFrom);        
    }

    public get dateToDateString(): string | undefined {
        return this._dateTimeToDateString(this.dateTo);
    }

    public get companyTaxId(): string | undefined {
        return this._request.company_tax_id ?? lastSbrReceiptStore.companyTaxId;
    }

    public get other(): string | undefined {
        return this._request.other ?? lastSbrReceiptStore.other;
    }

    public setDateFrom(value: string | undefined) {
        this._request.date_from = value;
    }

    public setDateTo(value: string | undefined) {
        this._request.date_to = value;
    }

    public setCompanyTaxId(value: string | undefined) {
        this._request.company_tax_id = value;
    }

    public setOther(value: string | undefined) {
        this._request.other = value;
    }

    public getRequest(): ISbrReceiptRequest {
        return {
            ...this._request,
            company_tax_id: this.companyTaxId,
            other: this.other,
        };
    }

    public async calculateAndGenerateReceipt(callback: () => void | undefined) {
        try {
            this._isGeneratingInvoice = true;
            const response = await this.service.generateSbrReceipt(this.getRequest());
            runInAction(() => {
                lastSbrReceiptStore.fetchFreshLatestSbrReceipt();
                if (response.encoded_pdf) {
                    const fileName = `sbr-receipt_${this.dateFromDateString}_${this.dateFromDateString}.pdf`;
                    downloadPdfFromBase64String(response.encoded_pdf, fileName);
                    toastMessageListStore.add({
                        title: 'Receipt configured',
                        description: 'Receipt was downloaded to your browser.',
                    });
                }
                this._isGeneratingInvoice = false;
            });

            /** Call callback if provided */
            if (callback) {
                callback();
            }
        } catch (error) {
            runInAction(() => {
                this._isGeneratingInvoice = false;
            });
        }
    }

    private _dateTimeToDateString(dateTime: string | undefined): string | undefined {
        if (!dateTime) {
            return undefined;
        }
        const date = new Date(dateTime);
        return date.toISOString().split('T')[0];
    }
}

export default SbrReceiptRequestStore;

const downloadPdfFromBase64String = (base64Url: string, fileName: string) => {
    if (!base64Url || !fileName) {
        console.error('Invalid base64Url or fileName');
        return;
    }
    
    const base64Prefix = 'data:application/pdf;base64,';
    if (!base64Url.startsWith(base64Prefix)) {
        base64Url = `${base64Prefix}${base64Url}`;
    }
    const link = document.createElement("a");
    link.href = base64Url;
    link.download = fileName;
    link.click();
};